import { Component, State, h, Host } from '@stencil/core';
import state from '@ugc-store';
import { handleErrorMessage, MIN_CHARACTERS, MAX_CHARACTERS_200, stringFormat, showToast } from '@ugc-utils/utils';
import { ReportAbuseType } from '@ugc-containers/ugc-report-abuse/interfaces';
import { postAbuseReport } from '@ugc-api/report-abuse';
import { modalZIndex } from '@ugc-utils/modal-z-index';

@Component({
  tag: 'ugc-report-abuse-modal',
  shadow: false,
})
export class UgcReportAbuseModal {
  @State() reasonType: string = '';
  @State() otherReason: string = '';
  @State() isTextareaError: boolean = false;
  @State() textareaMessage: string = '';
  @State() toastMessage: string = '';

  // async report abuse
  async handlePostReportAbuse() {
    try {
      const {
        reportAbuseModal: { type, referenceId },
      } = state;
      await postAbuseReport({
        type,
        referenceId,
        data: {
          reason: this.otherReason,
          reasonType: this.reasonType,
        },
      });
      showToast({
        type: 'success',
        message: [this.getLanguage().IDS_UGC_REPORT_ABUSE_SUCCESS || ''],
      });
      this.hide();
    } catch (e) {
      handleErrorMessage(e);
    }
  }

  getLanguage = () => {
    const {
      reportAbuseModal: { type },
    } = state;
    return type === ReportAbuseType.Review ? state.reviewStaticLanguage : state.qnaStaticLanguage;
  };

  hide = () => {
    this.setActiveValue('');
    state.isShowReportAbuseModal = false;
  };

  setActiveValue = value => {
    this.reasonType = value;
    this.otherReason = '';
    this.textareaMessage = '';
    this.isTextareaError = false;
    this.toastMessage = '';
  };

  isValidated = () => {
    this.toastMessage = '';
    if (!this.reasonType) {
      this.toastMessage = this.getLanguage().IDS_UGC_REPORT_ABUSE_TOAST_SELECT_REASON;
      return false;
    }
    const { reportAbuseReasonList } = state;
    if (this.reasonType === `${reportAbuseReasonList[reportAbuseReasonList.length - 1].value}`) {
      const otherReason = this.otherReason.trim().length;
      if (!otherReason) {
        this.toastMessage = this.getLanguage().IDS_UGC_REPORT_ABUSE_TOAST_ENTER_REASON;
        return false;
      }
      return this.handleValidate(this.otherReason);
    }
    return true;
  };

  handleValidate = value => {
    const otherReasonLength = value.trim().length;
    if (!otherReasonLength) {
      this.isTextareaError = true;
      this.textareaMessage = this.getLanguage().IDS_UGC_REPORT_ABUSE_TOAST_ENTER_REASON;
      return false;
    }
    if (otherReasonLength > 0 && otherReasonLength < MIN_CHARACTERS) {
      this.isTextareaError = true;
      this.textareaMessage = this.getLanguage().IDS_UGC_QNA_TEXTAREA_LEAST_CHARACTERS;
      return false;
    }
    if (otherReasonLength > MAX_CHARACTERS_200) {
      this.isTextareaError = true;
      this.textareaMessage = stringFormat(this.getLanguage().IDS_UGC_QNA_TEXTAREA_MORE_CHARACTERS, MAX_CHARACTERS_200);
      return false;
    }
    this.isTextareaError = false;
    this.textareaMessage = '';
    return true;
  };

  // click report
  handleReport = () => {
    const isValidated = this.isValidated();
    if (!isValidated) {
      if (this.toastMessage) {
        showToast({
          type: 'error',
          message: [this.toastMessage],
        });
      }
      return;
    }
    this.handlePostReportAbuse();
  };

  // change radio
  onRadioChange = value => {
    this.setActiveValue(value);
  };
  // change textarea
  onInputChange = ({ detail: value }) => {
    this.handleValidate(value);
    this.otherReason = value;
  };

  getView = () => {
    const {
      IDS_UGC_REPORT_ABUSE_GUIDE,
      IDS_UGC_REPORT_ABUSE_WRITE_REASON,
      IDS_UGC_QNA_MODAL_SUBMIT,
      IDS_MY_REVIEW_CANCEL_EDIT,
    } = this.getLanguage();
    const { isDesktop, reportAbuseReasonList } = state;
    return (
      <div class="report-abuse-wrap" style={!isDesktop && { minHeight: '70vh' }}>
        <div class={`report-abuse-content ${isDesktop ? 'px-32' : 'px-24'} mb-24`}>
          <p class="fs-14 c-666 mb-16">{IDS_UGC_REPORT_ABUSE_GUIDE}</p>
          <div class={`${isDesktop && 'px-10'}`}>
            <ugc-radio-group value={this.reasonType} radioChange={this.onRadioChange} options={reportAbuseReasonList} />
            <div class="report-abuse-other-reason">
              <ugc-textarea
                textareaName={IDS_UGC_REPORT_ABUSE_WRITE_REASON}
                height={161}
                textareaValue={this.otherReason}
                isError={this.isTextareaError}
                isShowErrorIcon={false}
                errorMessage={this.textareaMessage}
                onInputChange={this.onInputChange}
                disabled={this.reasonType !== `${reportAbuseReasonList[reportAbuseReasonList.length - 1].value}`}
              />
            </div>
          </div>
        </div>
        {isDesktop && (
          <div class={`report-abuse-button flex flex-row-reverse gap-16 px-32 pb-24`}>
            <ugc-button
              type="primary"
              height="48"
              fontWeight="bold"
              fontSize="16"
              onClick={this.handleReport}
              class="grow-1"
            >
              {IDS_UGC_QNA_MODAL_SUBMIT}
            </ugc-button>
            <ugc-button type="ghost" height="48" fontWeight="bold" fontSize="16" onClick={this.hide} class="grow-1">
              {IDS_MY_REVIEW_CANCEL_EDIT}
            </ugc-button>
          </div>
        )}
      </div>
    );
  };

  render() {
    const { isDesktop, isShowReportAbuseModal } = state;
    const { IDS_UGC_QNA_MODAL_SUBMIT, IDS_BTN_REPORT_ABUSE } = this.getLanguage();
    const view = isShowReportAbuseModal && this.getView();
    return (
      <Host>
        {isDesktop ? (
          <ugc-modal-dialog
            isShowCloseButton={true}
            isShow={isShowReportAbuseModal}
            modalTitle={IDS_BTN_REPORT_ABUSE}
            width={600}
            zIndex={modalZIndex['ugc-report-abuse-modal']}
            handleCancel={this.hide}
          >
            {view}
          </ugc-modal-dialog>
        ) : (
          <ugc-drawer
            isShow={isShowReportAbuseModal}
            isShowButton={true}
            isShowCloseButton={true}
            modalTitle={IDS_BTN_REPORT_ABUSE}
            zIndex={modalZIndex['ugc-report-abuse-modal']}
            submitButtonText={IDS_UGC_QNA_MODAL_SUBMIT}
            type="primary"
            handleSubmit={this.handleReport}
            handleCancel={this.hide}
            isGray
          >
            {view}
          </ugc-drawer>
        )}
      </Host>
    );
  }
}
